import React, { useEffect, useRef, useState } from "react";
import parse from "html-react-parser";
import { QRCodeSVG } from "qrcode.react/lib";

import {
  ERROR_MESSAGE,
  ERROR_MESSAGE_SUPPORT,
} from "../../../common/helpersTxt";
import {
  cleanSpecialCharacters,
  extractNumbersFromString,
} from "../../../extras";
// APP COMPONENTS
import NewFiscalForm from "./NewFiscalForm";
// SERVICES
import FiscalService from "../../../api/fiscal";
// MATERIAL UI COMPONENTS
import {
  Alert,
  Backdrop,
  Box,
  CircularProgress,
  Collapse,
  Fade,
  IconButton,
  Modal,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { grey, red, teal } from "@mui/material/colors";

// ICONS
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { useReactToPrint } from "react-to-print";
import { dispatchSnackBarMessage } from "../../../utils/snackbar";
import { validateArrayIncludes } from "../../../common/validators";

// Styled components
const StyledBadge = styled(Box)(({ bgcolor = grey[200] }) => ({
  display: "inline-block",
  padding: "4px 10px",
  borderRadius: "16px",
  backgroundColor: bgcolor,
  margin: "8px 0",
}));

// Print wrapper component to ensure proper rendering for printing
const PrintWrapper = styled(Box)({
  width: "100%",
  display: "block",
  "@media print": {
    width: "100%",
    margin: 0,
    padding: 0,
  },
});

// Modal container styled component
const ModalContainer = styled(Paper)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "70%",
  maxWidth: "900px",
  maxHeight: "90vh",
  backgroundColor: "#fff",
  borderRadius: "12px",
  boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  outline: "none",
  [theme.breakpoints.down("sm")]: {
    width: "95%",
    maxHeight: "95vh",
  },
}));

// Modal header component
const ModalHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: theme.spacing(2, 3),
  borderBottom: "1px solid rgba(0,0,0,0.08)",
  background: "linear-gradient(145deg, #ffffff, #f5f7fa)",
}));

// Modal content component
const ModalContent = styled(Box)({
  flexGrow: 1,
  overflowY: "auto",
  overflowX: "hidden",
  padding: "24px",
});

// Error alert component
const ErrorAlert = ({ open, message, onClose }) => {
  return (
    <Collapse in={open}>
      <Alert
        severity="error"
        action={
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            onClick={onClose}
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
        }
        sx={{ mb: 2 }}
      >
        {typeof message === "string" ? message : JSON.stringify(message)}
      </Alert>
    </Collapse>
  );
};

export default function FiscalInfoModal({
  orderId,
  orderValue,
  orderIndex,
  orderStatusNumber,
  customerName,
  customerIdentification,
  show,
  updateOrderStatus,
  closeModal,
}) {
  const printRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [fiscal, setFiscal] = useState(null);
  const [isValidatingRequest, setIsValidatingRequest] = useState(false);
  const [htmlContent, setHtmlContent] = useState("");
  const [isPdfReady, setIsPdfReady] = useState(false);
  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState(ERROR_MESSAGE);
  const [printReady, setPrintReady] = useState(false);

  // Constants for retry logic
  const RETRY_DELAY_MS = 5000; // 5 seconds
  const MAX_RETRY_ATTEMPTS = 3;

  // Track when content is ready for printing
  useEffect(() => {
    if (htmlContent && fiscal && !isValidatingRequest) {
      setPrintReady(true);
    } else {
      setPrintReady(false);
    }
  }, [htmlContent, fiscal, isValidatingRequest]);

  // Handle printing
  const handlePrint = useReactToPrint({
    // Direct ref instead of callback function
    contentRef: printRef,

    // Pre-flight check before printing
    onBeforePrint: async () => {
      if (!printRef.current || !htmlContent) {
        throw new Error("Content not ready for printing");
      }
    },

    // Error handling
    onPrintError: (errorLocation, error) => {
      console.error(`Print failed at ${errorLocation}`, error);
      showErrorMessage("Falha na impressão. Tente novamente.");
    },

    // Success callback
    onAfterPrint: () => {
      console.log("Print completed successfully");
    },

    // Document title for the print dialog
    documentTitle: `NF ${orderId || "Documento"}`,

    // Custom print styles
    pageStyle: `
      @media print {
        body { margin: 0; padding: 0; }
        .print-content { page-break-inside: avoid; }
      }
    `,
  });

  // Show error message
  const showErrorMessage = (message = ERROR_MESSAGE_SUPPORT) => {
    setErrorMessage(
      typeof message === "string" ? message : JSON.stringify(message)
    );
    setOpenErrorAlert(true);
  };

  // Utility function to delay execution
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Retry logic for loading fiscal data after generation
  const retryLoadFiscalData = async (attemptNumber = 1) => {
    try {
      console.log(
        `Attempting to load fiscal data - Attempt ${attemptNumber}/${MAX_RETRY_ATTEMPTS}`
      );

      const res = await FiscalService.getOrderFiscal(orderId);

      if (res.data) {
        console.log("Fiscal data loaded successfully");
        return res.data;
      }

      throw new Error("No fiscal data returned");
    } catch (error) {
      console.error(`Attempt ${attemptNumber} failed:`, error);

      if (attemptNumber < MAX_RETRY_ATTEMPTS) {
        console.log(`Waiting ${RETRY_DELAY_MS}ms before next attempt...`);
        await delay(RETRY_DELAY_MS);
        return retryLoadFiscalData(attemptNumber + 1);
      }

      throw error;
    }
  };

  // Get validation status
  const getValidationStatus = async () => {
    try {
      if (fiscal.status === "erro_autorizacao") {
        return;
      }
      await FiscalService.getNFEStatus(fiscal.reference);
      setIsValidatingRequest(false);
      await loadData();
    } catch (e) {
      showErrorMessage(e.response?.data?.error || "Error checking NFE status");
    }
  };

  // Load fiscal data
  const loadData = async () => {
    try {
      setLoading(true);
      setPrintReady(false);

      const res = await FiscalService.getOrderFiscal(orderId);
      setFiscal(res.data);

      if (res.data.danfe_path) {
        const res_danfe = await FiscalService.getFiscalTicketDANFE(
          res.data.danfe_path
        );

        if (res.data?.ticket_type === 2) {
          setIsPdfReady(true);
        }

        setHtmlContent(res_danfe.data);
      } else if (res.data?.ticket_type === 2) {
        setIsValidatingRequest(true);
      }
    } catch (e) {
      if (e.response?.data?.error !== "FiscalTicket not found") {
        showErrorMessage(
          e.response?.data?.error || "Error loading fiscal data"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Generate new fiscal with retry logic
  const generateNewFiscal = async (type, identification, extraInfo = null) => {
    try {
      setLoading(true);

      const fiscalObj = {
        orderId: orderId,
        identification: extractNumbersFromString(identification),
        ...extraInfo,
      };

      let response;
      if (type === 2) {
        response = await FiscalService.generateFiscalNFCE(fiscalObj);
      } else {
        response = await FiscalService.generateFiscalNFE(fiscalObj);
      }

      const { data } = response;

      if (data.status === "erro_autorizacao") {
        return dispatchSnackBarMessage(
          `fiscal-ticket-error-${new Date()}`,
          `Erro ao emitir nota fiscal: ${data.mensagem_sefaz}`,
          "error"
        );
      }

      if (validateArrayIncludes([], orderStatusNumber))
        updateOrderStatus("finish");

      try {
        await delay(RETRY_DELAY_MS);
        const fiscalData = await retryLoadFiscalData();

        setFiscal(fiscalData);

        if (fiscalData.danfe_path) {
          const danfeResponse = await FiscalService.getFiscalTicketDANFE(
            fiscalData.danfe_path
          );

          if (fiscalData?.ticket_type === 2) {
            setIsPdfReady(true);
          }

          setHtmlContent(danfeResponse.data);
        } else if (fiscalData?.ticket_type === 2) {
          setIsValidatingRequest(true);
        }

        console.log("Fiscal data retrieved and processed successfully");
      } catch (retryError) {
        console.error(
          "Failed to load fiscal data after all retry attempts:",
          retryError
        );
        showErrorMessage(
          "Nota fiscal gerada, mas houve erro ao carregar os dados. Tente recarregar a página."
        );

        // Fallback to original loadData method
        await loadData();
      }
    } catch (error) {
      console.error("Error generating fiscal:", error);
      if (error.response?.data.missing_products) {
        return showErrorMessage(
          `${error.response?.data.missing_products.join()} sem dados fiscais, para emitir notas fiscais todos os produtos do pedido devem conter dados fiscais cadastrados na aba Cardápios`
        );
      }

      if (error.response?.data?.error !== "FiscalTicket not found") {
        showErrorMessage(
          error.response?.data?.error ||
            error.response?.data?.mensagem ||
            "Error generating fiscal"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Initial data load
  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        loadData();
      }, 10);

      return () => clearTimeout(timer);
    }
  }, [show, orderId]);

  // Validation status polling
  useEffect(() => {
    let intervalId;

    if (isValidatingRequest) {
      intervalId = setInterval(() => {
        getValidationStatus();
      }, 5000);
    }

    return () => clearInterval(intervalId);
  }, [isValidatingRequest]);

  const renderPrintButton = () => {
    if (fiscal?.status === "cancelado") return null;

    return (
      <Box
        sx={{
          display: "flex",
          p: 2,
          justifyContent: "center",
        }}
      >
        <Tooltip title="Imprimir">
          <IconButton
            onClick={
              fiscal.ticket_type === 2 && isPdfReady
                ? () =>
                    window.open(
                      `data:application/pdf;base64,${htmlContent}`,
                      "_blank"
                    )
                : handlePrint
            }
            color="primary"
            disabled={!printReady}
          >
            <LocalPrintshopIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  // Render the content based on loading state
  const renderContent = () => {
    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            my: 8,
            py: 4,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    const shouldShowNewFiscalForm =
      !fiscal ||
      fiscal.status === "erro_autorizacao" ||
      fiscal.status === "cancelado";

    if (shouldShowNewFiscalForm) {
      return (
        <NewFiscalForm
          index={orderIndex}
          value={orderValue}
          identification={customerIdentification ?? ""}
          customerName={customerName}
          orderStatusNumber={orderStatusNumber}
          submit={generateNewFiscal}
        />
      );
    }

    // Show validation message when validating
    if (
      isValidatingRequest &&
      fiscal.ticket_type === 2 &&
      fiscal.status !== "erro_autorizacao"
    ) {
      return (
        <Stack
          spacing={2}
          sx={{ display: "flex", alignItems: "center", my: 6, py: 4 }}
        >
          <CircularProgress />
          <Typography variant="body2" align="center">
            NF-e em processo de validação, por favor aguarde alguns minutos e
            recarregue a janela.
          </Typography>
        </Stack>
      );
    }

    // For PDF type
    if (fiscal.ticket_type === 2 && isPdfReady) {
      return (
        <Box sx={{ height: "100%" }}>
          {renderPrintButton()}

          {/* PDF Embed */}
          <embed
            height="500px"
            width="100%"
            src={`data:application/pdf;base64,${htmlContent}`}
            type="application/pdf"
          />
        </Box>
      );
    }

    // For HTML/DANFE type
    return (
      <>
        {renderPrintButton()}

        {/* HTML content for printing */}
        <PrintWrapper ref={printRef}>
          {htmlContent ? (
            <div className="print-content">
              {parse(htmlContent)}
              {fiscal?.qrcode_url && (
                <div
                  id="qr-code0"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <QRCodeSVG value={fiscal.qrcode_url} />
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: "none" }}>No content available</div>
          )}
        </PrintWrapper>
      </>
    );
  };

  return (
    <Modal
      open={show}
      onClose={closeModal}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
        sx: {
          backgroundColor: "rgba(0,0,0,0.5)",
          backdropFilter: "blur(3px)",
        },
      }}
      sx={{
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Fade in={show}>
        <ModalContainer>
          {/* Modal Header */}
          <ModalHeader>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <ReceiptIcon color="primary" sx={{ mr: 1.5 }} />
              <Typography variant="h6" fontWeight="600">
                {fiscal ? "" : "EMITIR"} NOTA FISCAL
              </Typography>
            </Box>

            <IconButton onClick={closeModal} color="default">
              <CloseIcon />
            </IconButton>
          </ModalHeader>

          {/* Modal Content */}
          <ModalContent>
            <ErrorAlert
              open={openErrorAlert}
              message={errorMessage}
              onClose={() => setOpenErrorAlert(false)}
            />

            {fiscal && fiscal.status !== "erro_autorizacao" && (
              <>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <StyledBadge
                    bgcolor={
                      fiscal.status !== "cancelado" ? teal[200] : red[400]
                    }
                  >
                    <Typography sx={{ color: "white", fontWeight: 500 }}>
                      {cleanSpecialCharacters(fiscal.status).toUpperCase()}
                    </Typography>
                  </StyledBadge>
                </Box>

                <Typography
                  align="center"
                  sx={{
                    color: grey[700],
                    mb: 2,
                    fontStyle: "italic",
                    fontSize: "0.9rem",
                  }}
                >
                  {fiscal.sefaz_message || fiscal.mensagem_sefaz}
                </Typography>
              </>
            )}

            {renderContent()}
          </ModalContent>
        </ModalContainer>
      </Fade>
    </Modal>
  );
}
